import type { SupabaseClient } from '@supabase/supabase-js';
import { uuidSchema, type OwnedItem } from '@garagegymbros/contracts';
import { unwrap } from './client.js';

export interface CommunityWorkflowFeatures {
  flexDurationHours: readonly (24 | 48)[];
  equipmentQuickAdd: boolean;
  staffPhotoNotifications: boolean;
}
export async function getCommunityWorkflowFeatures(
  client: SupabaseClient,
  signal?: AbortSignal,
): Promise<CommunityWorkflowFeatures> {
  const unavailable: CommunityWorkflowFeatures = {
    flexDurationHours: [24],
    equipmentQuickAdd: false,
    staffPhotoNotifications: false,
  };
  try {
    const result = await client
      .rpc('community_workflow_features')
      .abortSignal(signal ?? AbortSignal.timeout(5000));
    const value = result.data;
    if (
      result.error ||
      !value ||
      !Array.isArray(value.flex_duration_hours) ||
      value.flex_duration_hours.length !== 2 ||
      value.flex_duration_hours[0] !== 24 ||
      value.flex_duration_hours[1] !== 48 ||
      typeof value.equipment_quick_add !== 'boolean' ||
      typeof value.staff_photo_notifications !== 'boolean'
    )
      return unavailable;
    return {
      flexDurationHours: [24, 48],
      equipmentQuickAdd: value.equipment_quick_add,
      staffPhotoNotifications: value.staff_photo_notifications,
    };
  } catch {
    return unavailable;
  }
}

export interface EquipmentQuickAddInput {
  requestId: string;
  productId: string;
  gymId?: string;
  expectedOwnerId: string;
}
export interface EquipmentQuickAddResult {
  item: OwnedItem;
  activityId: string;
  audience: 'public' | 'followers' | 'private';
  created: boolean;
}
export async function quickAddOwnedEquipment(
  client: SupabaseClient,
  input: EquipmentQuickAddInput,
  signal?: AbortSignal,
): Promise<EquipmentQuickAddResult> {
  const requestId = uuidSchema.parse(input.requestId),
    productId = uuidSchema.parse(input.productId),
    ownerId = uuidSchema.parse(input.expectedOwnerId),
    gymId = input.gymId ? uuidSchema.parse(input.gymId) : null;
  const request = client.rpc('quick_add_owned_equipment', {
    p_request_id: requestId,
    p_product_id: productId,
    p_gym_id: gymId,
    p_expected_owner_id: ownerId,
  });
  const value = unwrap<Record<string, any>>(await (signal ? request.abortSignal(signal) : request));
  const item = value?.item;
  if (
    !item ||
    item.owner_id !== ownerId ||
    item.product_id !== productId ||
    item.ownership_status !== 'owned' ||
    item.visibility !== 'public' ||
    !uuidSchema.safeParse(item.id).success ||
    !uuidSchema.safeParse(item.gym_id).success ||
    (gymId && item.gym_id !== gymId) ||
    !uuidSchema.safeParse(value.activity_id).success ||
    !['public', 'followers', 'private'].includes(value.audience) ||
    typeof value.created !== 'boolean'
  )
    throw new Error(
      'The equipment add response did not match your current request. Refresh your gym before trying again.',
    );
  return {
    item: item as OwnedItem,
    activityId: value.activity_id,
    audience: value.audience,
    created: value.created,
  };
}
